import { smtpConfigurationSchema } from '../../../../schemas/systemConfiguration.schema';
import toast from 'react-hot-toast';
import { TOAST_SUCCESS_IDS, TOAST_ERROR_IDS } from '../../../../interaces/toast.ids.interface';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { fornameTheme } from '../../../../services/helpers';
import { smtpSystemConfigurationsApi } from '../../api/smtpSystemConfigurations.api';
import { MODE } from '../../../../interaces/mode.interface';
import Select from 'react-select';
import { TYPE_SECURITY_SELECT } from '../../interface/smtpSystemConfigurations.interface';
import { FaEye, FaEyeSlash, FaSpinner, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { updateDataWhenCreate, updateDataWhenUpdate } from '../../../../services/helpers';
import styles from '../../styles/styles.module.css';
function ModalSmtpSystemConfigurations({
  mode = MODE.CREATE,
  model = null,
  onClose = () => {},
  setEntities = () => {},
}) {
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [whereChanges, setWhereChanges] = useState(false);
  const [configOk, setConfigOk] = useState(false);
  const [doingTest, setDoingTest] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    resolver: zodResolver(smtpConfigurationSchema),
  });

  useEffect(() => {
    if (model && mode === MODE.UPDATE) {
      reset({
        sender: model.sender,
        senderEmail: model.senderEmail,
        serverSmtp: model.serverSmtp,
        port: model.port,
        typeSecurity: model.typeSecurity,
        userSmtp: model.userSmtp,
      });
    }
  }, [model, mode, reset]);

  // ==========================================
  // DETECTAR CAMBIOS EN EL FORMULARIO
  // ==========================================

  useEffect(() => {
    if (isDirty) {
      setWhereChanges(true);
      setConfigOk(false);
    } else {
      setWhereChanges(false);
    }
  }, [isDirty]);

  // ==========================================
  // GUARDAR CONFIGURACIÓN SMTP
  // ==========================================

  const onSubmitSmtp = async (data) => {
    setLoading(true);

    try {
      if (mode === MODE.CREATE) {
        const response = await smtpSystemConfigurationsApi.create(data);
        updateDataWhenCreate(setEntities, response);
      } else {
        const response = await smtpSystemConfigurationsApi.update(model.id, data);
        updateDataWhenUpdate(setEntities, response);
      }
      toast.success(
        mode === MODE.CREATE
          ? TOAST_SUCCESS_IDS.RECORD_SAVED_SUCCESSFULLY
          : TOAST_SUCCESS_IDS.RECORD_UPDATED_SUCCESSFULLY,
        { id: TOAST_SUCCESS_IDS.RECORD_SAVED_SUCCESSFULLY }
      );

      setConfigOk(false);
      setWhereChanges(false);
      onClose();
    } catch (error) {
      toast.error(
        (error.message && error.message !== 'Error'
          ? error.message
          : TOAST_ERROR_IDS.ERROR_PERFORMING_THIS_ACTION) ||
          TOAST_ERROR_IDS.ERROR_PERFORMING_THIS_ACTION,
        { id: TOAST_ERROR_IDS.TWO }
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // PROBAR CONEXIÓN SMTP
  // ==========================================

  const testSmtp = async (data) => {
    setDoingTest(true);
    try {
      const res = await smtpSystemConfigurationsApi.testSmtp(data);

      if (res?.success) {
        setConfigOk(true);
        toast.success(TOAST_SUCCESS_IDS?.SMTP_TEST_OK || 'Conexión verificada con éxito');
      } else {
        setConfigOk(false);
        toast.error(res?.message || 'No se pudo verificar la conexión');
      }
    } catch (error) {
      toast.error(error.message || 'Error, verifica los datos');
    } finally {
      setDoingTest(false);
    }
  };

  const changeIsOkTest = () => {
    setConfigOk(false);
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content  modal-sizes">
        <div className="modal-header">
          <h2 className="modal-title">Configuración SMTP</h2>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Cerrar modal"
          >
            <FaTimes />
          </button>
        </div>
        <form className="modal-form" onSubmit={handleSubmit(onSubmitSmtp)}>
          <div className="modal-body">
            <div className={styles.divTextStart}>
              <p className={styles.textStart1}>Administración del servidor de correo saliente.</p>
              <p className={styles.textStart2}>Configura los datos de conexión SMTP.</p>
            </div>
            <div className="modal-div-columns">
              <div className="modal-div-column">
                <label className="label-base">
                  Remitente <p className="modal-input-required">*</p>
                </label>
                <input
                  type="text"
                  maxLength={100}
                  {...register('sender', {
                    onChange: (event) => {
                      fornameTheme(event);
                      changeIsOkTest();
                    },
                  })}
                  placeholder="Remitente"
                  className="input-base"
                />
                {errors.sender && <span className="error-text">{errors.sender.message}</span>}
              </div>

              <div className="modal-div-column">
                <label className="label-base">
                  Email <p className="modal-input-required">*</p>
                </label>
                <input
                  type="email"
                  maxLength={100}
                  {...register('senderEmail', { onChange: changeIsOkTest })}
                  placeholder="Email"
                  className="input-base"
                />
                {errors.senderEmail && (
                  <span className="error-text">{errors.senderEmail.message}</span>
                )}
              </div>

              <div className="modal-div-column">
                <label className="label-base">
                  Servidor SMTP (Host) <p className="modal-input-required">*</p>
                </label>
                <input
                  type="text"
                  maxLength={100}
                  {...register('serverSmtp', { onChange: changeIsOkTest })}
                  placeholder="smtp.gmail.com"
                  className="input-base"
                />
                {errors.serverSmtp && (
                  <span className="error-text">{errors.serverSmtp.message}</span>
                )}
              </div>

              <div className="modal-div-column">
                <label className="label-base">
                  Puerto <p className="modal-input-required">*</p>
                </label>
                <input
                  type="number"
                  {...register('port', {
                    valueAsNumber: true,
                    onChange: (event) => {
                      if (event.target.value.length > 5) {
                        event.target.value = event.target.value.slice(0, 5);
                      }
                      changeIsOkTest();
                    },
                  })}
                  placeholder="Ej: 587,465,35"
                  className="input-base"
                />
                {errors.port && <span className="error-text">{errors.port.message}</span>}
              </div>

              <div className="modal-div-column">
                <label className="label-base">
                  Tipo de Cifrado / Seguridad <p className="modal-input-required">*</p>
                </label>
                <Controller
                  name="typeSecurity"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={TYPE_SECURITY_SELECT}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.name.toString()}
                      value={
                        TYPE_SECURITY_SELECT.find(
                          (option) => option.value === String(field.value)
                        ) || null
                      }
                      onChange={(option) => {
                        field.onChange(option?.value);
                        changeIsOkTest();
                      }}
                      placeholder="Tipo de seguridad"
                      isSearchable={false}
                    />
                  )}
                />
                {errors.typeSecurity && (
                  <span className="error-text">{errors.typeSecurity.message}</span>
                )}
              </div>

              <div className="modal-div-column">
                <label className="label-base">
                  Usuario <p className="modal-input-required">*</p>
                </label>
                <input
                  type="text"
                  maxLength={100}
                  {...register('userSmtp', { onChange: changeIsOkTest })}
                  placeholder="Usuario"
                  className="input-base"
                />
                {errors.userSmtp && <span className="error-text">{errors.userSmtp.message}</span>}
              </div>

              <div className="modal-div-column">
                <label className="label-base">
                  Contraseña <p className="modal-input-required">*</p>
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    maxLength={100}
                    {...register('password', { onChange: changeIsOkTest })}
                    placeholder="Contraseña"
                    className="input-base pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword((visible) => !visible)}
                    aria-label={showCurrentPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    className="btn-password"
                  >
                    {showCurrentPassword ? <FaEyeSlash size={17} /> : <FaEye size={17} />}
                  </button>
                </div>
                {errors.password && <span className="error-text">{errors.password.message}</span>}
              </div>

              {configOk && (
                <div className="modal-div-column-one">
                  <p className="config-ok">¡Configuración verificada con éxito!</p>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={handleSubmit(testSmtp)}
              disabled={doingTest}
              className={styles.buttonFooterTest}
            >
              {doingTest ? <FaSpinner className="animate-spin" /> : <FaSyncAlt />}
              {doingTest ? 'Haciendo test' : 'Probar conexión'}
            </button>
            <button
              type="submit"
              disabled={loading || (!whereChanges && mode === MODE.UPDATE) || !configOk}
              className="btn-primary"
            >
              {loading ? 'Guardando...' : mode === MODE.CREATE ? 'Guardar' : 'Actualizar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ModalSmtpSystemConfigurations;
